import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { toastConfiguration } from '../Constant/Data';
import {
  applyDefaultLocation,
  applyPositionToActiveLocation,
  geolocationErrorMessage,
  getActiveLocation,
  getLocationDisplayName,
  isDefaultLocation as checkDefault,
  isLiveLocation as checkLive,
  requestBrowserPosition,
  subscribeLocationChange,
} from '../services/locationManager';

const WeatherLocationContext = createContext(undefined);

export function WeatherLocationProvider({ children }) {
  const [activeLocation, setActiveLocationState] = useState(() => getActiveLocation());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const activatingRef = useRef(false);

  useEffect(() => {
    const saved = getActiveLocation();
    if (!saved?.lat) {
      const def = applyDefaultLocation();
      setActiveLocationState(def);
    }
    const unsub = subscribeLocationChange((loc) => {
      setActiveLocationState({ ...loc });
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  const activateCurrentLocation = useCallback(async () => {
    if (activatingRef.current) return null;
    activatingRef.current = true;
    setLoading(true);
    setError(null);

    // Must start geolocation synchronously (same turn as the click) — awaiting anything
    // before this causes Chrome to return code 1 even when site permission is "granted".
    const positionPromise = requestBrowserPosition();

    try {
      const position = await positionPromise;
      const loc = await applyPositionToActiveLocation(position);
      setActiveLocationState({ ...loc });
      const label = getLocationDisplayName(loc);
      toast.success(`Location updated to ${label}`, toastConfiguration);
      return loc;
    } catch (err) {
      const msg = await geolocationErrorMessage(err);
      setError(msg);
      toast.error(msg, toastConfiguration);
      console.error('[WeatherLocation] activateCurrentLocation failed:', err);
      return null;
    } finally {
      activatingRef.current = false;
      setLoading(false);
    }
  }, []);

  const resetToDefault = useCallback(() => {
    const def = applyDefaultLocation();
    setActiveLocationState({ ...def });
    setError(null);
  }, []);

  const isDefault = checkDefault(activeLocation);
  const isLive = checkLive(activeLocation);
  const displayName = getLocationDisplayName(activeLocation);

  return (
    <WeatherLocationContext.Provider
      value={{
        activeLocation,
        loading,
        error,
        isDefault,
        isLive,
        displayName,
        activateCurrentLocation,
        resetToDefault,
      }}
    >
      {children}
    </WeatherLocationContext.Provider>
  );
}

export function useWeatherLocation() {
  const ctx = useContext(WeatherLocationContext);
  if (!ctx) {
    throw new Error('useWeatherLocation must be used inside <WeatherLocationProvider>');
  }
  return ctx;
}
